import { message, Tabs, TabsProps } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TEXT_BUTTON_HEADER_LIST } from '../../../config/config';
import { PermissionAdministrator } from '../../../permissions/PermissionAdministrator';
import { PermissionAuthorityGroups } from '../../../permissions/PermissionAuthorityGroups';
import { PermissionRoles } from '../../../permissions/PermissionRoles';
import administratorStore from '../../../stores/administratorStore';
import authoritiesStore from '../../../stores/authoritiesStore';
import authStore from '../../../stores/authStore';
import rolesStore from '../../../stores/rolesStore';
import unsavedStore from '../../../stores/unsavedStore';
import { mergeAuthorities } from '../../../utils/functionAuthorities';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import NotPermissionComponent from '../../atoms/NotPermissionComponent';
import ContentBox from '../../molecules/ContentBox';
import LoadingComponent from '../../molecules/LoadingComponent';
import Permission from '../../molecules/Permission';
import { useAuth } from '../../protected/ProviderAuth';
import FormAdministrator from './FormAdministrator';
import AddAdministrator from './AddAdministrator';
import AddCustomer from './AddCustomer';
import { editSchema, validationSchema } from './schema';
import customerStore from '../../../stores/customerStore';

const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <Link to="/administradores">Listagem de Administradores</Link>,
    },
    {
        title: <p>Cadastro de Administradores</p>,
    },
];

const CreateAdministrator: React.FC = observer(() => {
    const { uuid } = useParams<{ uuid: string }>();
    const { merchant } = authStore;
    const { administrator, loading } = administratorStore;
    const { account } = authStore;
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState<any>([]);
    const { hasPermission } = useAuth();
    const [selectedItems, setSelectedItems] = useState<any>([]);
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [role, setRole] = useState();


    useEffect(() => {
        const defaultparams = {
            size: 999,
            page: 0,
            sort: 'created,desc',
            search: '',
        }

        const fetchRolesData = async () => {
            await rolesStore.getList(defaultparams);
        };

        const fetchData = async () => {
            const response = await authoritiesStore.getList();

            // Filtra as permissões que devem ser exibidas
            const permissionsForSelectedGroup = response.filter((auth: any) => auth.show === true && auth.groupedBy === 'Produtos');
            setPermissions(permissionsForSelectedGroup);
        };

        const fetchPerfilData = async () => {
            if (uuid && hasPermission(PermissionAdministrator.GET_UUID)) {
                const result = await administratorStore.getAdministratorByUuid(uuid);
                const authoritiesList = mergeAuthorities(result.user);
                setSelectedItems(authoritiesList);

                if (result && result?.role.hierarchy) {
                    administratorStore.getEligibleParent(result?.role.hierarchy);
                }
            }
        };

        fetchPerfilData();

        if (hasPermission(PermissionAuthorityGroups.GET)) {
            fetchData();
        }

        if (hasPermission(PermissionRoles.GET)) {
            fetchRolesData();
        }

        return () => {
            administratorStore.reset();
        };
    }, [uuid, account]);

    const initialValues = {
        name: administrator?.name || '',
        username: administrator?.user?.username || '',
        email: administrator?.user?.email || '',
        password: '',
        confirmEmail: administrator?.user?.email || '',
        role: administrator?.role?.name ? { label: administrator?.role?.name, value: administrator?.role?.uuid, option: administrator?.role } : '',
        active: administrator?.active || '',
        parentAdministrator: administrator?.parentAdministrator?.name ? { label: administrator?.parentAdministrator?.name, value: administrator?.parentAdministrator?.uuid, option: administrator?.parentAdministrator } : '',
    };


    const handleSubmit = async (values: any) => {
        let response;

        const payload = {
            ...administrator,
            deleted: false,
            active: values.active || false,
            name: values.name,
            user: {
                ...administrator?.user,
                active: values.active || false,
                deleted: false,
                username: values.email,
                email: values.email,
                password: values.password || "temp",
                authorities: [],
                authorityGroups: []
            },
            role: { uuid: values?.role?.value },
            parentAdministrator: values?.parentAdministrator?.value ? { uuid: values?.parentAdministrator?.value } : null,
            merchant: merchant,
            username: values.username,
        }

        // ao fazer a conversão da lista de roles para { label: , value: } no select usando a função transformToValueLabel
        // existe uma terceira opção criada (option) que permite acessar o objeto inteiro (role) selecionado
        const authorityGroups = values.role.option.authorityGroups;

        if (uuid) {
            response = await administratorStore.editAdministrator(payload, uuid, authorityGroups);
        } else {
            response = await administratorStore.createAdministrator(payload, authorityGroups);
        }

        if (!response.error) listRouter();
    };

    const listRouter = () => {
        navigate(`/administradores`);
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'Informação Geral',
            children: <FormAdministrator setRole={setRole} setPermissions={setPermissions} permissions={permissions} setSelectedItems={setSelectedItems} selectedItems={selectedItems} />,
        },
        {
            key: '2',
            label: 'Adicionar Administradores',
            disabled: !administrator,
            children: <AddAdministrator administrator={administrator} />,
        },
        {
            key: '3',
            label: 'Adicionar Clientes',
            disabled: !administrator,
            children: <AddCustomer administrator={administrator} />,
        },
    ];

    const onChange = (key: string) => {
        setActiveTabKey(key);
    };

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionAdministrator.GET} textButton={TEXT_BUTTON_HEADER_LIST} onClick={() => listRouter()} />

            {loading &&
                <ContentBox className="mb-4 p-6" title="Cadastro de Administradores" description='Tela para cadastro de Administradores'>
                    <LoadingComponent />
                </ContentBox>
            }

            {!loading &&
                <Permission
                    permissionKeys={[PermissionAdministrator.POST, PermissionAdministrator.PUT, PermissionAdministrator.GET_UUID]}
                    fallback={NotPermissionComponent()}
                >
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={uuid ? editSchema : validationSchema}
                    >
                        {({ dirty }) => {
                            useEffect(() => {
                                unsavedStore.updateUnsavedChanges(dirty);
                            }, [dirty]);

                            return (
                                <Form>
                                    <Tabs
                                        className="mb-0"
                                        type="card"
                                        activeKey={activeTabKey}
                                        onChange={onChange}
                                        items={items}
                                    />

                                    <div className="flex justify-end pt-4">
                                        <Permission permissionKeys={[PermissionAdministrator.PUT, PermissionAdministrator.POST]} buttonType>
                                            <ActionButton htmlType="submit" actionType="primary">{uuid ? 'Atualizar' : 'Cadastrar'}</ActionButton>
                                        </Permission>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </Permission>
            }
        </>
    );
});

export default CreateAdministrator;
